import {
  portalAction,
  PortalThunk,
  checkoutDataSelector,
  startedCheckoutSelector,
  updateCheckout,
  setSelectedShippingOption,
  shippingAddressSelector,
  AsyncAction,
  clearShippingOptionError,
  setShippingOptionStatus,
  CheckoutAppDispatch,
  isPickupSelector,
} from '..'
import { ShippingOption, ShippingOptionStatus, ShippingOptionUpdate, ShippingOptionUpdateValidation, ShippingOptionValidation } from '../../model'
import { convertToXbtShippingOption, convertToXbtShippingOptions, mapShippingOptions, mapShippingOptionUpdate, XbtInfo } from '../../utils/shipping-options'
import { isValidShippingOptions, isValidShippingOptionUpdate } from '../../utils/validation'
import { Callback } from '../../utils/merchant'

interface ShippingOptionCallback {
  callback: Callback
  argument: any
}

interface ShippingOptionReturn {
  allShippingOptions: ShippingOption[]
  xbtAllShippingOptions: ShippingOption[]
}

interface UpdatedShippingOptionReturn {
  shippingOption: ShippingOptionUpdate
  xbtShippingOption: ShippingOptionUpdate
}

export const handleShippingOptions = ({ response, amount, isPickup, exchangeRate, consumerAmount }): ShippingOptionReturn | ShippingOptionValidation => {
  const options = mapShippingOptions(response)
  const hasError = isValidShippingOptions(options, amount, isPickup)

  let xbtAllShippingOptions = []
  if (exchangeRate) {
    const xbtInfo: XbtInfo = { exchangeRate, currency: consumerAmount.currency }
    const transformedOptions = hasError?.allShippingOptions ?? options
    // @ts-expect-error: OPERATION BLEED STOPPER
    xbtAllShippingOptions = convertToXbtShippingOptions(transformedOptions, xbtInfo)
  }

  if (hasError != null) {
    if (hasError.severity === 'warn') {
      const { allShippingOptions, ...others } = hasError
      return { ...others, xbtAllShippingOptions, allShippingOptions }
    }
    return hasError
  }

  return { allShippingOptions: options, xbtAllShippingOptions }
}

export const handleUpdatedShippingOption = ({
  response,
  amount,
  exchangeRate,
  consumerAmount,
}): UpdatedShippingOptionReturn | ShippingOptionUpdateValidation => {
  const option = mapShippingOptionUpdate(response)
  const hasError = isValidShippingOptionUpdate(option, amount)

  if (hasError != null) {
    return hasError
  }

  // @ts-expect-error: OPERATION BLEED STOPPER
  const updatedShippingOptionReturn: UpdatedShippingOptionReturn = { shippingOption: option, xbtShippingOption: undefined }
  if (exchangeRate) {
    const xbtInfo: XbtInfo = { exchangeRate, currency: consumerAmount.currency }
    updatedShippingOptionReturn.xbtShippingOption = convertToXbtShippingOption(option, xbtInfo)
  }

  return updatedShippingOptionReturn
}

export const getAllShippingOptions: PortalThunk<ShippingOptionCallback, ShippingOptionReturn | ShippingOptionValidation> = portalAction(
  'shippingOption',
  async (request, thunk) => {
    // @ts-expect-error: OPERATION BLEED STOPPER
    const { amount } = checkoutDataSelector(thunk.getState())
    const isPickup = isPickupSelector(thunk.getState())
    // @ts-expect-error: OPERATION BLEED STOPPER
    const { exchangeRate, consumerAmount } = startedCheckoutSelector(thunk.getState())
    const response = await request.callback(request.argument)

    return handleShippingOptions({ response, amount, isPickup, exchangeRate, consumerAmount })
  }
)

export const getUpdatedShippingOption: PortalThunk<ShippingOptionCallback, UpdatedShippingOptionReturn | ShippingOptionUpdateValidation | undefined> =
  portalAction('shippingOptionUpdate', async (request, thunk) => {
    // @ts-expect-error: OPERATION BLEED STOPPER
    const { amount } = checkoutDataSelector(thunk.getState())
    // @ts-expect-error: OPERATION BLEED STOPPER
    const { exchangeRate, consumerAmount } = startedCheckoutSelector(thunk.getState())
    const response = await request.callback(request.argument)

    if (!response) {
      thunk.dispatch(setShippingOptionStatus(ShippingOptionStatus.UpdatedSelected))
      return
    }
    return handleUpdatedShippingOption({ response, amount, exchangeRate, consumerAmount })
  })

export const selectAndUpdateShippingOption =
  (selectedOption: ShippingOption): AsyncAction<CheckoutAppDispatch> =>
  // @ts-expect-error TODO: fix type error
  async (dispatch, getState) => {
    const { chosenAddress } = shippingAddressSelector(getState())
    const isPickup = isPickupSelector(getState())

    await dispatch(clearShippingOptionError())

    await dispatch(setSelectedShippingOption(selectedOption))

    return await dispatch(
      updateCheckout({
        amount: selectedOption.orderAmount,
        shippingAddressId: isPickup ? undefined : chosenAddress.id,
        shippingOptionIdentifier: selectedOption.id,
      })
    )
  }
